import type { GraphQLError, GraphQLFormattedError } from 'graphql';

import type { AuthErrorCode } from './sessionClient';

const CODE_REAUTHENTICATION_REQUIRED = 'reauthentication-required';

/** Fallback copy for auth submissions that fail without a user-facing message. */
export const GENERIC_AUTH_FAILURE_MESSAGE = 'Something went wrong. Please try again.';

// Expected credential outcomes of a login attempt — the user mistyped or the
// account is (about to be) locked. These are surfaced in-form and are never
// worth an error reporter's attention, unlike network/5xx/unknown failures.
const EXPECTED_LOGIN_FAILURE_CODES: ReadonlySet<AuthErrorCode> = new Set([
  'invalid-credentials',
  'lockout-imminent',
  'locked',
]);

export const isExpectedLoginFailure = (code: AuthErrorCode): boolean =>
  EXPECTED_LOGIN_FAILURE_CODES.has(code);

export const isFetchResponseUnauthenticated = async (resp: Response): Promise<boolean> => {
  return resp.status === 401;
};

export const isFetchResponseUnauthorized = async (resp: Response): Promise<boolean> => {
  return resp.status === 403;
};

export const isFetchResponseReauthRequired = async (resp: Response): Promise<boolean> => {
  if (resp.status !== 403) {
    return false;
  }

  try {
    const json = await resp.json();
    return json?.metadata?.code === CODE_REAUTHENTICATION_REQUIRED || false;
  } catch {
    return false;
  }
};

export const isGraphQLErrorUnauthenticated = (
  error: GraphQLError | GraphQLFormattedError,
): boolean => {
  // TODO: Update Athena error serialization so we don't need to look at the stacktrace.
  // https://opendoor.atlassian.net/browse/SXP-587
  return error.message.startsWith('HTTP Error 401');
};

export const isGraphQLErrorUnauthorized = (
  error: GraphQLError | GraphQLFormattedError,
): boolean => {
  // TODO: Update Athena error serialization so we don't need to look at the stacktrace.
  // https://opendoor.atlassian.net/browse/SXP-587
  return error.message.startsWith('HTTP Error 403');
};

export const isGraphQLErrorReauthRequired = (
  error: GraphQLError | GraphQLFormattedError,
): boolean => {
  // TODO: Update Athena error serialization so we don't need to look at the stacktrace.
  // https://opendoor.atlassian.net/browse/SXP-587
  return (
    error.message.startsWith('HTTP Error 403') &&
    error.message.includes(`"code":"${CODE_REAUTHENTICATION_REQUIRED}"`)
  );
};
